import { ApplicationConfig, provideBrowserGlobalErrorListeners, ENVIRONMENT_INITIALIZER, inject } from '@angular/core';
import { provideRouter, withPreloading, withViewTransitions } from '@angular/router';
import { AuthenticatedPreloadingStrategy } from './core/strategies/authenticated-preloading.strategy';
import { provideHttpClient, withInterceptors, HttpClient } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { importProvidersFrom } from '@angular/core';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { MultiTranslateHttpLoader } from './core/loaders/multi-translate-http.loader';
import { provideToastr } from 'ngx-toastr';
import {
  SOCIAL_AUTH_CONFIG,
  SocialAuthServiceConfig,
  GoogleLoginProvider
} from '@abacritt/angularx-social-login';

import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { NavigationTrackingService } from './core/services/navigation-tracking.service';
import { environment } from '../environments/environment';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: () => inject(NavigationTrackingService).init()
    },
    provideRouter(routes, withPreloading(AuthenticatedPreloadingStrategy),
      withViewTransitions({
        onViewTransitionCreated: ({ from, to, transition }) => {
          const fromPath = from.routeConfig?.path;
          const toPath = to.routeConfig?.path;

          // The KYC pending page keeps mutating media/state during route changes,
          // which can cause Angular's view transition DOM update to time out.
          if (fromPath === 'kyc-pending' || toPath === 'kyc-pending') {
            transition.skipTransition();
          }
        }
      }),
    ),
    provideHttpClient(withInterceptors([authInterceptor])),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: (http: HttpClient) =>
            new MultiTranslateHttpLoader(http, './assets/i18n/', '.json', [
              'signin',
              'signup',
              'header'
            ]),
          deps: [HttpClient],
        },
      }),
    ),
    provideAnimations(),
    provideToastr({
      timeOut: 3000,
      positionClass: 'toast-top-right',
      preventDuplicates: true,
      progressBar: true,
      closeButton: true,
      newestOnTop: true
    }),
    {
      provide: SOCIAL_AUTH_CONFIG,
      useValue: {
        autoLogin: false,
        providers: [
          {
            id: GoogleLoginProvider.PROVIDER_ID,
            provider: new GoogleLoginProvider(environment.GOOGLE_CLIENT_ID, {
              oneTapEnabled: false
            })
          }
        ],
        onError: (err: any) => {
          console.error('Social login error:', err);
        }
      } as SocialAuthServiceConfig
    }
  ]
};
